import { useState } from "react";

/**
 * Wraps the async `defaultValues` fetcher of an edit form so a refused or failed
 * load becomes a state the surrounding modal can render.
 *
 * React Hook Form leaves `formState.isLoading` true when the promise rejects, so
 * a 403 from a gated route spins forever behind its toast. `loadFailed` is the
 * flag that lets the caller answer honestly instead.
 *
 * The rejection is re-thrown on purpose. The form must never mount with empty
 * values: a submit — or an auto-save, which needs no submit at all — would then
 * write that emptiness over the record it failed to read.
 * `apps/web/src/sites/calculations/calculation-editor/calculation-editor.tsx`
 * carries the same reasoning at its own form.
 */
export function useGuardedDefaultValues<T>(fetcher: () => Promise<T>) {
	/** State */
	const [loadFailed, setLoadFailed] = useState<boolean>(false);

	/** Functions */
	async function defaultValues(): Promise<T> {
		try {
			return await fetcher();
		} catch (error) {
			setLoadFailed(true);
			throw error;
		}
	}

	/** Return */
	return { defaultValues, loadFailed };
}
